import DeploymentsItem from '../../types/DeploymentsItem';
import styles from './BackupsListCreateDatastoreButton.module.less';
import { useEffect, useState } from 'react';
import { Button, Col, Row, Typography } from 'antd';
import { ExclamationCircleOutlined } from '@severalnines/frontend_hub/libs/icons';
import { AddDeploymentV2Props } from '../../services/ProvisionService';
import { useHistory } from 'react-router';
import CreateDatastorePopover from './CreateDatastorePopover';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { restoreDatastoreFromBackup } from '../../slices/restore.slice';
import { Draft } from '@reduxjs/toolkit';
import { RootState } from '../../redux/store';
import useDataStoreServices from '../../core/hooks/useDataStoreServices';
import { useParams } from 'react-router';
import DbService from '../../types/DbService';
import AppLoading from '../AppLoading';
import useCreditCardRequired from '../../core/hooks/useCreditCardRequired';

type BackupsListCreateDatastoreButtonProps = {
    deployment: Draft<DeploymentsItem>;
    record: any;
    actionIcon?: any;
};

export type UrlProps = {
    dataStoreUuid: string;
};

export default function BackupsListCreateDatastoreButton({
    deployment,
    record,
    actionIcon,
}: BackupsListCreateDatastoreButtonProps) {
    const { dataStoreUuid } = useParams<UrlProps>();
    const [openPopover, setOpenPopover] = useState<boolean>(false);
    const history = useHistory();
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state: RootState) => state.user);
    const { services } = useDataStoreServices({
        dataStoreUuid,
    });

    const { checkCreditCardAndExecute, hasValidCards, loading, modal } =
        useCreditCardRequired({
            title: 'Credit Card Required for Datastore Creation',
            description:
                'A valid credit card is required to create datastores from backups. Please add one to continue.',
        });

    const executeDatastoreCreation = async () => {
        try {
            const azs = getAzsFromNodes();
            const backupToCreateDatastore: AddDeploymentV2Props =
                deployment.getDatastoreCreationData(record.backupId, azs);
            dispatch(restoreDatastoreFromBackup(backupToCreateDatastore));

            history.push({
                pathname: `/projects/${user?.getOrganizationId()}/data-stores/add`,
            });
        } catch (e) {
            console.error('Oops errors!');
        }
    };

    const onCreateDatastore = async () => {
        // Check for credit cards first, then execute datastore creation
        await checkCreditCardAndExecute(executeDatastoreCreation);
    };

    const getAzsFromNodes = (): string[] => {
        const azs: string[] = [];
        services?.dbServices?.forEach((s: DbService) => {
            if (!azs.includes(s.getAvailabilityZone())) {
                azs.push(s.getAvailabilityZone());
            }
        });
        return azs;
    };

    const handleCancel = () => {
        setOpenPopover(!openPopover);
    };

    return (
        <>
            <CreateDatastorePopover
                isVisible={openPopover}
                setOpenPopover={handleCancel}
                actionIcon={actionIcon}
                content={
                    <div className={styles.PopoverContainer}>
                        <Row
                            justify="center"
                            className={styles.PopoverContentRow}
                        >
                            <Col sm={3}>
                                <ExclamationCircleOutlined
                                    className={
                                        styles.ExclamationCircleFilledColor
                                    }
                                    color="#ffac0a"
                                />
                            </Col>
                            <Col sm={21}>
                                <strong>
                                    Do you want to create a datastore from the
                                    backup?
                                </strong>
                                <Typography.Paragraph>
                                    The datastore is created on the same cloud
                                    and uses the same instance configuration.
                                    {!hasValidCards && (
                                        <>
                                            <br />
                                            <strong
                                                style={{ color: '#faad14' }}
                                            >
                                                Note: A valid credit card will
                                                be required to proceed.
                                            </strong>
                                        </>
                                    )}
                                </Typography.Paragraph>
                            </Col>
                        </Row>
                        <Row className={styles.PopoverActions} justify="center">
                            <Col sm={6}>
                                <Button
                                    className={styles.BorderRadius}
                                    size="small"
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                            </Col>
                            <Col sm={6}>
                                <Button
                                    type="primary"
                                    className={styles.BorderRadius}
                                    size="small"
                                    onClick={onCreateDatastore}
                                    loading={loading}
                                >
                                    Create
                                </Button>
                            </Col>
                        </Row>
                    </div>
                }
            />
            {modal}
        </>
    );
}
